import throttle from "lodash-es/throttle";
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { Vector3D } from "common/types";
import { MapModel, AssetModel, AssetModelType } from "common/model";
import { generatedMapToModels } from "features/builder/generatedMap";
import { MAX_UNDO_STACK_SIZE, MAP_MESH_SIZE } from "features/builder/constants";
import {
  DEFAULT_PIECE_LIST_UI,
  PieceListContext,
  PieceLayoutMode,
  PieceSort,
  PieceSortOrder,
  MineFilter,
  PublicFilterState,
} from "features/builder/pieceList";
import {
  getBuilderMaps,
  deleteBuilderMap,
  updateBuilderMap,
  createBuilderMap,
  getBuilderMapContent,
  createPiece,
  getDraftSnapshot,
  getEditHistory,
  savePieceContent,
  getPieceDetail,
  getPieceContent,
  getPieceCommands,
  getPieceHints,
  GeneratedMapData,
} from "features/builder/api";
import {
  BuilderMap,
  BuilderState,
  ActionRecord,
  PieceStatus,
  derivePieceStatus,
  BuilderCommandId,
  CommandLimits,
  DEFAULT_ENABLED_COMMANDS,
  CompletionStepId,
  ActionCommandType,
  PieceEditMeta,
  PieceMilestone,
  PieceEditHistoryEntry,
  ApiPieceListItem,
  PieceDraftSnapshot,
} from "features/builder/types";

// ビルダー起動時の既定アセットスロット（実ピース読込/作成時に使用）
const DEFAULT_BUILDER_SLOT: AssetModel[] = [
  { type: AssetModelType.DECORATION, name: "tree_desert", filename: "tree_desert.gltf.glb" },
  { type: AssetModelType.DECORATION, name: "tree_forest", filename: "tree_forest.gltf.glb" },
  {
    type: AssetModelType.RIVER,
    name: "tileMedium_teamBlue",
    filename: "tileMedium_teamBlue.gltf.glb",
  },
  { type: AssetModelType.GROUND, name: "tileLow_desert", filename: "tileLow_desert.gltf.glb" },
  {
    type: AssetModelType.GROUND,
    name: "tileMedium_forest",
    filename: "tileMedium_forest.gltf.glb",
  },
];

// POST /v1/pieces で新規ピースを作成し、作成された piece を返す
export const createNewPiece = createAsyncThunk(
  "builder/createNewPiece",
  async ({ uid, name }: { uid: string; name: string }) => await createPiece({ uid, name }),
);

// GET /v1/pieces/:id/draft で編集スナップショット（draft 有→draft、無→正本）を読み込む。
// 併せて編集履歴も取得してエディタに反映する。
// draft API は所有者検証で非作者に 404 を返すため、他人の公開ピース（みんなのピースの
// プレイ）では公開エンドポイント群から同形のスナップショットを合成する。
export const loadPiece = createAsyncThunk(
  "builder/loadPiece",
  async ({ piece_id, uid }: { piece_id: string; uid: string }) => {
    try {
      const snap = await getDraftSnapshot({ piece_id, uid });
      let history: PieceEditHistoryEntry[] = [];
      try {
        history = (await getEditHistory({ piece_id })).entries;
      } catch {
        history = [];
      }
      // 自分のピースを編集するときだけ、開いた区切りを記録に残す（Builder.tsx）
      return { snap, history, own: true };
    } catch {
      // まず存在確認（getPieceDetail）。404 ならここで throw → thunk reject → 編集画面に入らず redirect。
      // 残り（content/commands/hints）を撃つ前に弾くことで、存在しないピースでの 404 連発を防ぐ。
      const detail = await getPieceDetail({ piece_id, uid });
      const [content, commands, hints] = await Promise.all([
        getPieceContent({ piece_id }),
        getPieceCommands({ piece_id }),
        getPieceHints({ piece_id }).catch(() => ({ hints: ["", "", ""] })),
      ]);
      const snap: PieceDraftSnapshot = {
        piece_id,
        completed: detail.completed,
        published: detail.published,
        has_draft: false,
        name: detail.name,
        description: detail.description,
        tags: detail.tags,
        cover: detail.cover,
        capture: detail.capture,
        background: detail.background,
        size: content.size,
        block_count: content.block_count,
        map: content.map as MapModel[],
        enabled_commands: commands.enabled_commands,
        command_limits: commands.command_limits,
        hint: hints.hints,
        updated_at: content.updated_at,
      };
      return { snap, history: [] as PieceEditHistoryEntry[], own: false };
    }
  },
);

export const mapsAdapter = createEntityAdapter<BuilderMap>();
export const { selectAll: selectAllMaps, selectById: selectMapById } = mapsAdapter.getSelectors(
  (state: RootState) => state.builder.maps,
);

export const mapModelAdapter = createEntityAdapter<MapModel>();
export const {
  selectAll: selectAllMapModel,
  selectById: selectMapModelById,
  selectIds: selectMapModelIds,
} = mapModelAdapter.getSelectors((state: RootState) => state.builder.map);

// 座標データを 5 秒スロットルで /v1/pieces/:id/content に保存（編集中は draft 隔離）
export const savePieceContentThrottled = createAsyncThunk(
  "builder/savePieceContent",
  throttle(async (_arg: void, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const uid = state.user.active?.uid;
    const pieceId = state.builder.editingBuilderMapId;
    if (!uid || !pieceId || pieceId === "local-map") return;
    try {
      await savePieceContent({
        piece_id: pieceId,
        uid,
        size: state.builder.size,
        map: mapModelAdapter.getSelectors().selectAll(state.builder.map),
      });
    } catch (error) {
      console.error(error);
    }
  }, 5000),
);

// 配置モデルが指定サイズのグリッド範囲外かを判定
// セル index は配置式 position = cell * MAP_MESH_SIZE + MAP_MESH_SIZE/2 の逆算
const isOutOfBounds = (model: MapModel, size: Vector3D): boolean => {
  const cx = Math.floor(model.position.x / MAP_MESH_SIZE);
  const cz = Math.floor(model.position.z / MAP_MESH_SIZE);
  return cx < 0 || cz < 0 || cx >= size.x || cz >= size.z;
};

// 指定サイズに縮小した場合に削除される配置モデル数
export const countOutOfBounds = (models: MapModel[], size: Vector3D): number =>
  models.filter((m) => isOutOfBounds(m, size)).length;

// 完成ステップ判定（spec.md §5.8.3）
export const selectCompletionSteps = createSelector(
  [
    selectAllMapModel,
    (state: RootState) => state.builder.enabledCommands,
    (state: RootState) => state.builder.tested,
    (state: RootState) => state.builder.name,
    (state: RootState) => state.builder.description,
  ],
  (models, enabledCommands, tested, name, description): Record<CompletionStepId, boolean> => ({
    [CompletionStepId.CHARACTER]: models.some((m) => m.type === AssetModelType.CHARACTER),
    [CompletionStepId.GOAL]: models.some((m) => m.type === AssetModelType.GOAL),
    [CompletionStepId.COMMANDS]: models.length >= 5 && enabledCommands.length >= 1,
    [CompletionStepId.TESTED]: tested,
    [CompletionStepId.META]: name.trim().length > 0 && description.trim().length >= 10,
  }),
);

/**
 * じめんが1つでも置いてあるか。
 * キャラクターとゴールは、置いてある物の上にしか置けない。
 * じめんが無いとタップしても何も起きず、子どもには理由が分からないので、
 * 先に「おく」を押せないようにするために使う。
 */
export const selectHasGround = createSelector([selectAllMapModel], (models) =>
  models.some((m) => m.type === AssetModelType.GROUND),
);

export const selectCompletionCount = createSelector(
  [selectCompletionSteps],
  (steps) => Object.values(steps).filter(Boolean).length,
);

export const selectCanComplete = createSelector([selectCompletionCount], (count) => count === 5);

const initialState: BuilderState = {
  selectedMap: null,
  maps: mapsAdapter.getInitialState(),
  loading: true,
  editingBuilderMapId: null,
  size: { x: 5, y: 5, z: 5 },
  map: mapModelAdapter.getInitialState(),
  slot: Array(5).fill(null),
  undoStacks: [],
  redoStacks: [],
  actionMenu: {
    tap: true,
    asset: false,
    eraser: false,
    assetList: false,
  },
  selectedSlotIndex: null,
  selectedAssetModel: null,
  selectedPlacedModelId: null,
  name: "",
  description: "",
  hint: ["", "", ""],
  background: null,
  cover: null,
  enabledCommands: DEFAULT_ENABLED_COMMANDS,
  commandLimits: {},
  tested: false,
  status: PieceStatus.DRAFT_NEW,
  editHistory: [], // 実データは loadPiece で取得
  draftDirty: false,
  captureRequested: false,
  pendingMilestones: [],
  sceneLoadSeq: 0,
  listUi: DEFAULT_PIECE_LIST_UI,
  placeHint: null,
  pendingDetail: null,
};

/** restore で戻す なまえ・せつめいなどの、いまの値（区切りを残すときにも使う） */
export const currentPieceMeta = (state: BuilderState): PieceEditMeta => ({
  name: state.name,
  description: state.description,
  cover: state.cover,
  background: state.background,
  hint: [...state.hint],
  enabled_commands: [...state.enabledCommands],
  command_limits: { ...state.commandLimits },
});

const applyPieceMeta = (state: BuilderState, meta: PieceEditMeta) => {
  state.name = meta.name;
  state.description = meta.description;
  state.cover = meta.cover;
  state.background = meta.background;
  state.hint = [meta.hint[0] ?? "", meta.hint[1] ?? "", meta.hint[2] ?? ""];
  state.enabledCommands = [...meta.enabled_commands];
  state.commandLimits = meta.command_limits ?? {};
};

const builderSlice = createSlice({
  name: "builder",
  initialState,
  reducers: {
    updateSelectedMapById(state: BuilderState, action: PayloadAction<{ id: string }>) {
      const map = mapsAdapter.getSelectors().selectById(state.maps, action.payload.id);
      if (map) {
        state.selectedMap = map;
      }
    },
    updateLoading(state: BuilderState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    resetActionMenu(state: BuilderState) {
      Object.keys(state.actionMenu).forEach((k) => (state.actionMenu[k] = false));
      state.selectedSlotIndex = null;
      state.selectedAssetModel = null;
    },
    updateActionMenu(
      state: BuilderState,
      action: PayloadAction<{
        key: string;
        value: boolean;
        exclusive?: boolean;
      }>,
    ) {
      const { key, value, exclusive } = action.payload;
      state.actionMenu[key] = value;
      if (exclusive) {
        Object.keys(state.actionMenu)
          .filter((k) => k !== key)
          .forEach((k) => (state.actionMenu[k] = false));
      }
      if (key !== "eraser") {
        state.actionMenu.eraser = false;
      }
    },
    addModelToMap(state: BuilderState, action: PayloadAction<MapModel>) {
      mapModelAdapter.addOne(state.map, action.payload);
    },
    updateMapModel(
      state: BuilderState,
      action: PayloadAction<{
        id: string;
        changes: { [item: string]: string | Vector3D };
      }>,
    ) {
      mapModelAdapter.updateOne(state.map, {
        id: action.payload.id,
        changes: action.payload.changes,
      });
    },
    removeModelFromMap(state: BuilderState, action: PayloadAction<string>) {
      mapModelAdapter.removeOne(state.map, action.payload);
    },
    updateSlot(state: BuilderState, action: PayloadAction<AssetModel[]>) {
      state.slot = action.payload;
    },
    selectSlot(state: BuilderState, action: PayloadAction<number | null>) {
      state.selectedSlotIndex = action.payload;
    },
    selectedAssetMesh(state: BuilderState, action: PayloadAction<AssetModel | null>) {
      state.selectedAssetModel = action.payload;
    },
    selectPlacedModel(state: BuilderState, action: PayloadAction<string | null>) {
      state.selectedPlacedModelId = action.payload;
    },
    // 置けない所をタップしたときの案内を、その場所に出す
    showPlaceHint(
      state: BuilderState,
      action: PayloadAction<{ messageKey: string; x: number; y: number }>,
    ) {
      state.placeHint = { ...action.payload, seq: (state.placeHint?.seq ?? 0) + 1 };
    },
    clearPlaceHint(state: BuilderState) {
      state.placeHint = null;
    },
    // メッセージのリンクから詳細を開く（タブ指定つき）
    requestPieceDetail(
      state: BuilderState,
      action: PayloadAction<{ pieceId: string; tab?: string }>,
    ) {
      state.pendingDetail = action.payload;
    },
    clearPieceDetailRequest(state: BuilderState) {
      state.pendingDetail = null;
    },
    // API連携なしでローカル動作させるための初期化
    initializeLocalMap(state: BuilderState) {
      state.loading = true;
      state.size = { x: 5, y: 5, z: 5 };
      state.slot = [
        {
          type: AssetModelType.DECORATION,
          name: "tree_desert",
          filename: "tree_desert.gltf.glb",
        },
        {
          type: AssetModelType.DECORATION,
          name: "tree_forest",
          filename: "tree_forest.gltf.glb",
        },
        {
          type: AssetModelType.RIVER,
          name: "tileMedium_teamBlue",
          filename: "tileMedium_teamBlue.gltf.glb",
        },
        {
          type: AssetModelType.GROUND,
          name: "tileLow_desert",
          filename: "tileLow_desert.gltf.glb",
        },
        {
          type: AssetModelType.GROUND,
          name: "tileMedium_forest",
          filename: "tileMedium_forest.gltf.glb",
        },
      ];
      state.undoStacks = [];
      state.redoStacks = [];
      state.actionMenu = {
        tap: true,
        asset: false,
        eraser: false,
        assetList: false,
      };
      state.selectedSlotIndex = null;
      state.selectedAssetModel = null;
      state.editingBuilderMapId = "local-map";
      state.name = "";
      state.description = "";
      state.hint = ["", "", ""];
      state.enabledCommands = DEFAULT_ENABLED_COMMANDS;
      state.commandLimits = {};
      state.tested = false;
      state.status = PieceStatus.DRAFT_NEW;
      state.editHistory = []; // 新規ローカルマップは履歴なし
      state.draftDirty = false;
      mapModelAdapter.removeAll(state.map);
    },
    // マップサイズ変更（ビルダー画面でいつでも変更可。縮小時は範囲外モデルを削除）
    setMapSize(state: BuilderState, action: PayloadAction<Vector3D>) {
      const next = action.payload;
      const outIds = mapModelAdapter
        .getSelectors()
        .selectAll(state.map)
        .filter((m) => isOutOfBounds(m, next))
        .map((m) => m.id);
      mapModelAdapter.removeMany(state.map, outIds);
      state.size = next;
      state.draftDirty = true;
      // リサイズは undo/redo 対象外（座標系が変わるため）
      state.undoStacks = [];
      state.redoStacks = [];
    },
    // ピースビルダー: メタ・完成判定・ドラフト編集（spec.md §5.8）
    setPieceName(state: BuilderState, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    setPieceDescription(state: BuilderState, action: PayloadAction<string>) {
      state.description = action.payload;
    },
    setPieceBackground(state: BuilderState, action: PayloadAction<string | null>) {
      state.background = action.payload;
      state.draftDirty = true;
    },
    setPieceCover(state: BuilderState, action: PayloadAction<string | null>) {
      state.cover = action.payload;
    },
    setPieceHint(state: BuilderState, action: PayloadAction<{ level: number; value: string }>) {
      state.hint[action.payload.level] = action.payload.value;
    },
    setCommandSettings(
      state: BuilderState,
      action: PayloadAction<{ commands: BuilderCommandId[]; limits: CommandLimits }>,
    ) {
      state.enabledCommands = action.payload.commands;
      state.commandLimits = action.payload.limits;
    },
    markTested(state: BuilderState) {
      state.tested = true;
    },
    setPieceStatus(state: BuilderState, action: PayloadAction<PieceStatus>) {
      state.status = action.payload;
    },
    pushEditHistory(state: BuilderState, action: PayloadAction<PieceEditHistoryEntry>) {
      state.editHistory.push(action.payload);
    },
    setEditHistory(state: BuilderState, action: PayloadAction<PieceEditHistoryEntry[]>) {
      state.editHistory = action.payload;
    },
    // 指定 seq の地点へ restore する。その地点の snapshot をエディタへ適用する。
    // 後ろの記録は消さず、restore も 1 つの操作として undo で取り消せるようにする（docs/edit-history-undo.md）
    // なまえ・せつめい・カバー・はいけい・ヒント・つかえる コマンドも、区切りが持っていれば戻す（保存は savePieceMeta）
    restoreToHistoryEntry(state: BuilderState, action: PayloadAction<{ seq: number }>) {
      const target = state.editHistory.find((e) => e.seq === action.payload.seq);
      if (!target?.map) return;
      const previous = mapModelAdapter.getSelectors().selectAll(state.map);
      const previousSize = state.size;
      const previousMeta = currentPieceMeta(state);
      mapModelAdapter.setAll(state.map, target.map);
      if (target.size) state.size = target.size;
      if (target.meta) applyPieceMeta(state, target.meta);
      state.undoStacks = [
        ...state.undoStacks,
        {
          type: ActionCommandType.REPLACE_ALL,
          forward: {
            models: target.map,
            size: target.size ?? previousSize,
            meta: target.meta ?? undefined,
          },
          backward: {
            models: previous,
            size: previousSize,
            meta: target.meta ? previousMeta : undefined,
          },
          timestamp: Date.now(),
          label: "restore",
        },
      ].slice(-MAX_UNDO_STACK_SIZE);
      state.redoStacks = [];
      state.draftDirty = true;
    },
    // restore の undo / redo：マップと大きさ（と なまえなど）をまとめて差し替える（大きさの変更と違って undo の履歴は消さない）
    replaceMapState(
      state: BuilderState,
      action: PayloadAction<{ models: MapModel[]; size?: Vector3D; meta?: PieceEditMeta }>,
    ) {
      mapModelAdapter.setAll(state.map, action.payload.models);
      if (action.payload.size) state.size = action.payload.size;
      if (action.payload.meta) applyPieceMeta(state, action.payload.meta);
      state.draftDirty = true;
    },
    // 離脱時のサムネイル取得要求（シーン生存中に撮るため BuilderCapture が監視）
    requestCapture(state: BuilderState) {
      state.captureRequested = true;
    },
    clearCaptureRequest(state: BuilderState) {
      state.captureRequested = false;
    },
    // 3D の画面を作り直してから撮る区切りを頼む。いまより後に作り終えた画面を撮る
    requestMilestone(
      state: BuilderState,
      action: PayloadAction<{ action_type: PieceMilestone; payload?: Record<string, unknown> }>,
    ) {
      state.pendingMilestones.push({ ...action.payload, afterSeq: state.sceneLoadSeq });
    },
    shiftMilestone(state: BuilderState) {
      state.pendingMilestones.shift();
    },
    markSceneLoaded(state: BuilderState) {
      state.sceneLoadSeq += 1;
    },
    setDraftDirty(state: BuilderState, action: PayloadAction<boolean>) {
      state.draftDirty = action.payload;
    },
    // 一覧トップ 左パネル UI 状態（タブ・フィルタ・並び替え・ひょうじ・スクロール）
    setActiveListTab(state: BuilderState, action: PayloadAction<PieceListContext>) {
      state.listUi.activeTab = action.payload;
    },
    setMineFilter(state: BuilderState, action: PayloadAction<MineFilter>) {
      state.listUi.mine.filter = action.payload;
    },
    setPublicFilter(state: BuilderState, action: PayloadAction<PublicFilterState>) {
      state.listUi.public.filter = action.payload;
    },
    setListSort(
      state: BuilderState,
      action: PayloadAction<{ context: PieceListContext; axis: PieceSort; order: PieceSortOrder }>,
    ) {
      const ui = state.listUi[action.payload.context];
      ui.sortAxis = action.payload.axis;
      ui.sortOrder = action.payload.order;
    },
    setListLayoutMode(
      state: BuilderState,
      action: PayloadAction<{ context: PieceListContext; mode: PieceLayoutMode }>,
    ) {
      state.listUi[action.payload.context].layoutMode = action.payload.mode;
    },
    setListScroll(
      state: BuilderState,
      action: PayloadAction<{ context: PieceListContext; scrollTop: number }>,
    ) {
      state.listUi[action.payload.context].scrollTop = action.payload.scrollTop;
    },
    enterDraftEditMode(state: BuilderState) {
      state.status = PieceStatus.EDITING;
      state.draftDirty = true;
    },
    discardDraft(state: BuilderState) {
      state.status = PieceStatus.DRAFT_NEW;
      state.draftDirty = false;
    },
    // Undo/Redo reducers
    pushUndo(state: BuilderState, action: PayloadAction<ActionRecord>) {
      state.undoStacks.push(action.payload);
      // スタックサイズ制限
      if (state.undoStacks.length > MAX_UNDO_STACK_SIZE) {
        state.undoStacks.shift();
      }
    },
    popUndo(state: BuilderState) {
      state.undoStacks.pop();
    },
    pushRedo(state: BuilderState, action: PayloadAction<ActionRecord>) {
      state.redoStacks.push(action.payload);
    },
    popRedo(state: BuilderState) {
      state.redoStacks.pop();
    },
    clearRedo(state: BuilderState) {
      state.redoStacks = [];
    },
    clearHistory(state: BuilderState) {
      state.undoStacks = [];
      state.redoStacks = [];
    },
    // AI生成マップを適用
    applyGeneratedMap(state: BuilderState, action: PayloadAction<GeneratedMapData>) {
      const previous = mapModelAdapter.getSelectors().selectAll(state.map);
      const models = generatedMapToModels(action.payload);
      mapModelAdapter.setAll(state.map, models);
      // AI の適用も「もどす」で取り消せるように履歴へ積む
      state.undoStacks = [
        ...state.undoStacks,
        {
          type: ActionCommandType.REPLACE_ALL,
          forward: { models },
          backward: { models: previous },
          timestamp: Date.now(),
          label: "ai_generate",
        },
      ].slice(-MAX_UNDO_STACK_SIZE);
      state.redoStacks = [];
    },
    // マップ全体の差し替え（AI適用の もどす/やりなおす で使う）
    setAllMapModels(state: BuilderState, action: PayloadAction<MapModel[]>) {
      mapModelAdapter.setAll(state.map, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getBuilderMaps.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.setAll(
          state.maps,
          action.payload.filter((item) => !item.deleted_at),
        );
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(createBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.addOne(state.maps, action.payload);
        state.selectedMap = action.payload;
        state.loading = true;
        state.slot = [
          {
            type: AssetModelType.DECORATION,
            name: "tree_desert",
            filename: "tree_desert.gltf.glb",
          },
          {
            type: AssetModelType.DECORATION,
            name: "tree_forest",
            filename: "tree_forest.gltf.glb",
          },
          {
            type: AssetModelType.RIVER,
            name: "tileMedium_teamBlue",
            filename: "tileMedium_teamBlue.gltf.glb",
          },
          {
            type: AssetModelType.GROUND,
            name: "tileLow_desert",
            filename: "tileLow_desert.gltf.glb",
          },
          {
            type: AssetModelType.GROUND,
            name: "tileMedium_forest",
            filename: "tileMedium_forest.gltf.glb",
          },
        ];
        state.undoStacks = [];
        state.redoStacks = [];
        state.actionMenu = {
          tap: true,
          asset: false,
          eraser: false,
          assetList: false,
        };

        state.selectedSlotIndex = null;
        state.selectedAssetModel = null;

        state.editingBuilderMapId = action.payload.id;
        state.size = { x: 5, y: 5, z: 5 };
        state.hint = ["", "", ""];
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(updateBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.updateOne(state.maps, action.payload);
        state.selectedMap = action.payload;
      })
      .addCase(deleteBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<string>) => {
        mapsAdapter.removeOne(state.maps, action.payload);
      })
      .addCase(
        getBuilderMapContent.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: BuilderState, action: PayloadAction<any>) => {
          state.loading = true;
          state.slot = [
            {
              type: AssetModelType.DECORATION,
              name: "tree_desert",
              filename: "tree_desert.gltf.glb",
            },
            {
              type: AssetModelType.DECORATION,
              name: "tree_forest",
              filename: "tree_forest.gltf.glb",
            },
            {
              type: AssetModelType.RIVER,
              name: "tileMedium_teamBlue",
              filename: "tileMedium_teamBlue.gltf.glb",
            },
            {
              type: AssetModelType.GROUND,
              name: "tileLow_desert",
              filename: "tileLow_desert.gltf.glb",
            },
            {
              type: AssetModelType.GROUND,
              name: "tileMedium_forest",
              filename: "tileMedium_forest.gltf.glb",
            },
          ];
          state.undoStacks = [];
          state.redoStacks = [];
          state.actionMenu = {
            tap: true,
            asset: false,
            eraser: false,
            assetList: false,
          };

          state.selectedSlotIndex = null;
          state.selectedAssetModel = null;

          state.editingBuilderMapId = action.payload.id;
          const { content } = action.payload;
          state.size = content.size;
          // ヒントは別 API（GET /v1/pieces/:id/hints）。当面は空で初期化。
          // TODO(piece-builder): hints API 実装後にサーバ値で初期化する。
          state.hint = ["", "", ""];
          mapModelAdapter.setAll(state.map, content.map);
        },
      )
      // 新規ピース作成（POST /v1/pieces）→ 空のエディタ状態で開く
      .addCase(
        createNewPiece.fulfilled,
        (state: BuilderState, action: PayloadAction<ApiPieceListItem>) => {
          const piece = action.payload;
          state.editingBuilderMapId = piece.piece_id;
          state.name = piece.name ?? "";
          state.description = piece.description ?? "";
          state.hint = ["", "", ""];
          state.background = null;
          state.cover = null;
          state.enabledCommands = DEFAULT_ENABLED_COMMANDS;
          state.commandLimits = {};
          state.size = { x: 5, y: 5, z: 5 };
          state.status = PieceStatus.DRAFT_NEW;
          state.tested = false;
          state.draftDirty = false;
          state.undoStacks = [];
          state.redoStacks = [];
          state.editHistory = [];
          state.slot = DEFAULT_BUILDER_SLOT;
          state.actionMenu = { tap: true, asset: false, eraser: false, assetList: false };
          state.selectedSlotIndex = null;
          state.selectedAssetModel = null;
          mapModelAdapter.removeAll(state.map);
          state.loading = true;
        },
      )
      // 既存ピース読込（GET /v1/pieces/:id/draft）→ スナップショット＋編集履歴をエディタへ反映
      .addCase(
        loadPiece.fulfilled,
        (
          state: BuilderState,
          action: PayloadAction<{ snap: PieceDraftSnapshot; history: PieceEditHistoryEntry[] }>,
        ) => {
          const { snap, history } = action.payload;
          state.editingBuilderMapId = snap.piece_id;
          state.name = snap.name ?? "";
          state.description = snap.description ?? "";
          state.hint =
            Array.isArray(snap.hint) && snap.hint.length === 3 ? snap.hint : ["", "", ""];
          state.background = snap.background ?? null;
          state.cover = snap.cover ?? null;
          state.enabledCommands =
            Array.isArray(snap.enabled_commands) && snap.enabled_commands.length > 0
              ? (snap.enabled_commands as BuilderCommandId[])
              : DEFAULT_ENABLED_COMMANDS;
          state.commandLimits =
            snap.command_limits && typeof snap.command_limits === "object"
              ? snap.command_limits
              : {};
          state.size = snap.size ?? { x: 5, y: 5, z: 5 };
          state.status = derivePieceStatus({
            completed: snap.completed,
            published: snap.published,
            has_draft: snap.has_draft,
          });
          state.tested = false;
          state.draftDirty = false;
          state.captureRequested = false;
          state.pendingMilestones = [];
          state.undoStacks = [];
          state.redoStacks = [];
          state.editHistory = history ?? [];
          state.slot = DEFAULT_BUILDER_SLOT;
          state.actionMenu = { tap: true, asset: false, eraser: false, assetList: false };
          state.selectedSlotIndex = null;
          state.selectedAssetModel = null;
          mapModelAdapter.setAll(state.map, snap.map ?? []);
          state.loading = true;
        },
      );
  },
});

export const actions = { ...builderSlice.actions };

export default builderSlice.reducer;
